// src/utils/leagueRostersStore.js
// Store leggero (localStorage) per le rose della lega Fantacalcio.it importate
// in un'asta a listone. Le scrive il tab "Lega" (upload/incolla dell'export
// "Esporta rose"); le legge anche il tab "Squadra" per annotare il listone col
// possesso in lega. Le due viste sono montate insieme in AuctionDetailPage: un
// piccolo bus di eventi tiene "Squadra" allineata quando "Lega" (ri)importa.
//
// Persistenza: il localStorage da solo non bastava (dati persi uscendo e
// rientrando: è per dispositivo, e una scrittura oltre la quota fallisce in
// silenzio). Ora la fonte di verità è Firestore, in
// users/{ownerUid}/auctions/{auctionId}/leagueData/rosters (coperto dalla
// regola generale users/{uid}/**, nessuna modifica alle regole); il
// localStorage resta come cache per il primo render istantaneo.
// AuctionDetailPage chiama useLeagueRostersPersistence(ownerUid, auctionId,
// canWrite): scarica i dati salvati e registra dove rispecchiare le scritture.
import { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';

const ROSTERS_KEY = (auctionId) => `fantapp_league_rosters_${auctionId}`;
const LINKS_KEY = (auctionId) => `fantapp_league_playerlinks_${auctionId}`;
const EVENT = 'fantapp:league-rosters-changed';

// auctionId → { ownerUid, canWrite }: dove salvare su Firestore le scritture.
const remoteTargets = new Map();

function remoteRef(ownerUid, auctionId) {
  return doc(db, 'users', ownerUid, 'auctions', String(auctionId), 'leagueData', 'rosters');
}

// Salvati come stringa JSON: i nomi squadra/giocatore dell'export diventano
// chiavi di mappa, e come stringa non c'è rischio di caratteri non ammessi
// nei nomi dei campi Firestore. ~26 squadre × 25 giocatori ≈ poche decine di KB.
function persistRemote(auctionId, patch) {
  const target = remoteTargets.get(String(auctionId));
  if (!target?.ownerUid || !target.canWrite) return;
  const payload = { updatedAt: Date.now() };
  if ('rosters' in patch) payload.rostersJson = patch.rosters == null ? null : JSON.stringify(patch.rosters);
  if ('playerLinks' in patch) payload.playerLinksJson = patch.playerLinks == null ? null : JSON.stringify(patch.playerLinks);
  setDoc(remoteRef(target.ownerUid, auctionId), payload, { merge: true })
    .catch((err) => console.warn('Salvataggio rose lega non riuscito:', err));
}

function parseJsonField(value) {
  if (typeof value !== 'string') return null;
  try { return JSON.parse(value); } catch { return null; }
}

function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJSON(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); }
  catch { /* quota/private mode: ignora */ }
}

function notify(auctionId) {
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: { auctionId: String(auctionId) } })); }
  catch { /* SSR / ambienti senza window */ }
}

// Copia in memoria (per sessione) dell'ultimo valore noto: le letture la
// preferiscono al localStorage, che può rifiutare le scritture se è pieno.
const memoryCache = new Map(); // auctionId → { rosters, playerLinks }

function setMemory(auctionId, patch) {
  const id = String(auctionId);
  memoryCache.set(id, { ...(memoryCache.get(id) || {}), ...patch });
}

// ─── Rose della lega: { teams, order, totals, source, savedAt } | null ──────
export function readLeagueRosters(auctionId) {
  const mem = memoryCache.get(String(auctionId));
  if (mem && 'rosters' in mem) return mem.rosters;
  return readJSON(ROSTERS_KEY(auctionId), null);
}

export function writeLeagueRosters(auctionId, data) {
  const value = { ...data, savedAt: Date.now() };
  setMemory(auctionId, { rosters: value });
  writeJSON(ROSTERS_KEY(auctionId), value);
  persistRemote(auctionId, { rosters: value });
  notify(auctionId);
}

export function clearLeagueRosters(auctionId) {
  setMemory(auctionId, { rosters: null });
  try { localStorage.removeItem(ROSTERS_KEY(auctionId)); } catch { /* ignore */ }
  persistRemote(auctionId, { rosters: null });
  notify(auctionId);
}

// ─── Abbinamenti manuali export→dataset: { [excelNorm]: playerId | null } ───
export function readPlayerLinks(auctionId) {
  const mem = memoryCache.get(String(auctionId));
  if (mem && 'playerLinks' in mem) return mem.playerLinks || {};
  return readJSON(LINKS_KEY(auctionId), {});
}

export function writePlayerLinks(auctionId, links) {
  setMemory(auctionId, { playerLinks: links });
  writeJSON(LINKS_KEY(auctionId), links);
  persistRemote(auctionId, { playerLinks: links });
  notify(auctionId);
}

export function clearPlayerLinks(auctionId) {
  setMemory(auctionId, { playerLinks: {} });
  try { localStorage.removeItem(LINKS_KEY(auctionId)); } catch { /* ignore */ }
  persistRemote(auctionId, { playerLinks: null });
  notify(auctionId);
}

// ─── Persistenza Firestore ──────────────────────────────────────────────────
// Montato una volta da AuctionDetailPage per le aste a listone. Al mount:
//  - se su Firestore c'è qualcosa → lo copia in cache locale e avvisa le viste;
//  - se su Firestore non c'è nulla ma in locale sì (dati importati prima di
//    questa modifica) → li carica su Firestore, così non vanno persi.
// canWrite=false (asta di un altro utente, sola lettura): solo cache locale.
export function useLeagueRostersPersistence(ownerUid, auctionId, canWrite) {
  useEffect(() => {
    if (!ownerUid || !auctionId) return undefined;
    const id = String(auctionId);
    remoteTargets.set(id, { ownerUid, canWrite: Boolean(canWrite) });
    if (!canWrite) return () => { remoteTargets.delete(id); };

    let cancelled = false;
    getDoc(remoteRef(ownerUid, id))
      .then((snap) => {
        if (cancelled) return;
        const data = snap.exists() ? snap.data() : null;
        const hasRemote = data && ('rostersJson' in data || 'playerLinksJson' in data);
        if (!hasRemote) {
          const localRosters = readLeagueRosters(id);
          const localLinks = readPlayerLinks(id);
          if (localRosters || Object.keys(localLinks || {}).length) {
            persistRemote(id, { rosters: localRosters, playerLinks: localLinks });
          }
          return;
        }
        const rosters = parseJsonField(data.rostersJson);
        const links = parseJsonField(data.playerLinksJson);
        if (rosters) writeJSON(ROSTERS_KEY(id), rosters);
        else { try { localStorage.removeItem(ROSTERS_KEY(id)); } catch { /* ignore */ } }
        if (links) writeJSON(LINKS_KEY(id), links);
        else { try { localStorage.removeItem(LINKS_KEY(id)); } catch { /* ignore */ } }
        // Il localStorage può essere pieno: le viste ricevono comunque i dati
        // di Firestore tramite la cache in memoria.
        memoryCache.set(id, { rosters, playerLinks: links || {} });
        notify(id);
      })
      .catch((err) => console.warn('Lettura rose lega non riuscita:', err));

    return () => {
      cancelled = true;
      remoteTargets.delete(id);
    };
  }, [ownerUid, auctionId, canWrite]);
}

// ─── Sottoscrizione (same-tab via CustomEvent, cross-tab via storage) ───────
export function subscribeLeagueRosters(auctionId, callback) {
  const id = String(auctionId);
  const onCustom = (e) => { if (!e?.detail || String(e.detail.auctionId) === id) callback(); };
  const onStorage = (e) => {
    if (!e || (e.key !== ROSTERS_KEY(id) && e.key !== LINKS_KEY(id))) return;
    memoryCache.delete(id); // scritto da un'altra scheda: vale il localStorage
    callback();
  };
  window.addEventListener(EVENT, onCustom);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVENT, onCustom);
    window.removeEventListener('storage', onStorage);
  };
}

// ─── Hook: rilegge quando qualcuno (ri)importa o cancella ───────────────────
export function useLeagueRosters(auctionId) {
  const [snapshot, setSnapshot] = useState(() => ({
    rosters: readLeagueRosters(auctionId),
    playerLinks: readPlayerLinks(auctionId),
  }));

  useEffect(() => {
    setSnapshot({
      rosters: readLeagueRosters(auctionId),
      playerLinks: readPlayerLinks(auctionId),
    });
    return subscribeLeagueRosters(auctionId, () => {
      setSnapshot({
        rosters: readLeagueRosters(auctionId),
        playerLinks: readPlayerLinks(auctionId),
      });
    });
  }, [auctionId]);

  return snapshot;
}
